import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Check, X, ExternalLink, Mail } from "lucide-react";

export default function AdminClaimsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("pending");

  useEffect(() => {
    if (user === false) navigate("/login", { state: { from: "/admin/claims" } });
    else if (user && user.role !== "admin") navigate("/", { replace: true });
  }, [user, navigate]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/claims");
      setClaims(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { if (user && user.role === "admin") load(); }, [user]);

  const decide = async (c, action) => {
    try {
      await api.post(`/admin/claims/${c.id}/decide`, { action });
      toast(action === "approve" ? "Claim approved — owner can now respond to reviews." : "Claim rejected.");
      await load();
    } catch (e) {
      toast("Couldn't decide", { description: formatApiError(e.response?.data?.detail) || e.message });
    }
  };

  if (user && user.role !== "admin") return null;
  const filtered = claims.filter((c) => tab === "all" ? true : c.status === tab);

  return (
    <div className="max-w-5xl mx-auto px-6 lg:px-10 py-12 pb-24" data-testid="admin-claims-page">
      <p className="text-xs uppercase tracking-[0.25em] text-[#786B63]">Editor desk</p>
      <h1 className="font-serif text-4xl sm:text-5xl mt-3 leading-tight">Ownership claims</h1>
      <p className="text-[#786B63] mt-3 max-w-xl">Café owners requesting verification. Approve to give them the ability to respond to reviews.</p>

      <div className="mt-8 border-b border-border flex gap-8">
        {["pending", "approved", "rejected", "all"].map((s) => (
          <button
            key={s}
            onClick={() => setTab(s)}
            data-testid={`claims-tab-${s}`}
            className={`pb-3 -mb-px text-sm tracking-wide uppercase ${tab === s ? "text-[#C05A46] border-b-2 border-[#C05A46]" : "text-[#786B63]"}`}
          >
            {s} · {s === "all" ? claims.length : claims.filter((c) => c.status === s).length}
          </button>
        ))}
      </div>

      <div className="mt-8 space-y-4">
        {loading ? (
          [...Array(2)].map((_, i) => <div key={i} className="border border-border bg-card rounded-sm h-32 animate-pulse" />)
        ) : filtered.length === 0 ? (
          <div className="border border-border bg-card rounded-sm p-10 text-center">
            <p className="font-serif text-2xl">Nothing here.</p>
            <p className="text-sm text-[#786B63] mt-1">No claims in this tab.</p>
          </div>
        ) : filtered.map((c) => (
          <div key={c.id} data-testid={`claim-${c.id}`} className="border border-border bg-card rounded-sm p-5">
            <div className="flex items-start justify-between flex-wrap gap-4">
              <div className="flex-1 min-w-0">
                <p className="text-xs uppercase tracking-[0.2em] text-[#786B63]">
                  {c.status} · {new Date(c.created_at).toLocaleDateString()}
                </p>
                <h3 className="font-serif text-2xl mt-1">{c.cafe_name}</h3>
                <p className="text-sm text-[#2C1E16] mt-2">
                  <span className="text-[#786B63]">By:</span> {c.user_name} ({c.user_email}) · Role: {c.role}
                </p>
                <p className="text-sm text-[#786B63] mt-1 inline-flex items-center gap-1.5">
                  <Mail size={12} strokeWidth={1.5} /> Business email: {c.business_email}
                </p>
                <p className="text-sm text-[#2C1E16]/90 mt-3 leading-relaxed whitespace-pre-line">
                  <span className="text-[#786B63]">Verification:</span> {c.evidence}
                </p>
                <a
                  href={`/cafe/${c.cafe_id}`}
                  target="_blank"
                  rel="noreferrer"
                  className="text-xs text-[#C05A46] inline-flex items-center gap-1 mt-3"
                >
                  <ExternalLink size={12} strokeWidth={1.5} /> View café page
                </a>
              </div>
              {c.status === "pending" && (
                <div className="flex md:flex-col gap-2">
                  <Button
                    size="sm"
                    onClick={() => decide(c, "approve")}
                    className="rounded-sm bg-[#C05A46] hover:bg-[#A34A38] text-[#F9F6F0]"
                    data-testid={`approve-claim-${c.id}`}
                  >
                    <Check className="h-4 w-4 mr-1.5" strokeWidth={1.5} /> Approve
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => decide(c, "reject")}
                    className="rounded-sm"
                    data-testid={`reject-claim-${c.id}`}
                  >
                    <X className="h-4 w-4 mr-1.5" strokeWidth={1.5} /> Reject
                  </Button>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
